/**
 * Bella cart drawer: delivery region, date & gift sheets.
 * Uses capture-phase delegation on #mini-cart so clicks work after every section re-render.
 */
(function () {
  const formControllers = new WeakMap();

  function getMiniCartHost() {
    return document.getElementById('mini-cart');
  }

  function getForm() {
    const host = getMiniCartHost();
    return host && host.querySelector('form#cart.bella-cart, form.bella-cart#cart, form.bella-cart');
  }

  function getCartDrawerSectionId() {
    return document.querySelector('mini-cart')?.dataset?.sectionId || 'mini-cart';
  }

  function sectionInnerFromHtml(html, selector) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector || '.shopify-section');
    return node ? node.innerHTML : '';
  }

  function updateCartIconSections(parsed) {
    if (!parsed || !parsed.sections) return;
    ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach((id) => {
      const el = document.getElementById(id);
      const block = parsed.sections[id];
      if (el && block) {
        el.innerHTML = sectionInnerFromHtml(block, '.shopify-section');
      }
    });
  }

  /** Full drawer HTML replace — use for add-to-cart / line-item changes, not Bella attribute patches. */
  function renderCartSections(parsed) {
    const host = getMiniCartHost();
    if (!parsed || !parsed.sections || !host) return;

    const sid = getCartDrawerSectionId();
    const miniHtml = parsed.sections[sid];
    if (!miniHtml) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[Bella cart] Missing sections[' + sid + ']. Keys:', parsed.sections ? Object.keys(parsed.sections) : []);
      }
      return;
    }

    host.innerHTML = sectionInnerFromHtml(miniHtml, '.shopify-section');
    document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
    updateCartIconSections(parsed);
  }

  /** Persist delivery date / gift / region without re-rendering the drawer (avoids jitter & animation replay). */
  function syncBellaCartAfterAttributeUpdate(parsed) {
    const form = getForm();
    if (!form) return;
    if (parsed && typeof parsed.total_price === 'number') {
      form.dataset.subtotalCents = String(parsed.total_price);
    }
    const ctrl = formControllers.get(form);
    if (ctrl) {
      ctrl.updateFooterTotals();
    }
  }

  function cartUpdateBody(extra, opts) {
    opts = opts || {};
    const payload = { ...extra };
    if (!opts.attributesOnly) {
      const sid = getCartDrawerSectionId();
      payload.sections = [sid, 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
      payload.sections_url = window.location.pathname;
    }
    return JSON.stringify(payload);
  }

  const rules = window.BellaDeliveryRules || {};
  const startOfDay = rules.startOfDay || function (d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const addDays = rules.addDays || function (d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };
  const isoDate = rules.isoDate || function (d) {
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };
  const parseISODate = rules.parseISODate || function (s) {
    if (!s) return null;
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };
  const getMinSelectableDate = function (region) {
    if (rules.getCartMinSelectableDate) return rules.getCartMinSelectableDate(region);
    if (rules.getMinSelectableDate) return rules.getMinSelectableDate(region, 'cart');
    return startOfDay(new Date());
  };
  const isBeforeMinSelectable = function (date, region) {
    if (rules.isBeforeMinSelectable) return rules.isBeforeMinSelectable(date, region, 'cart');
    return startOfDay(date).getTime() < getMinSelectableDate(region).getTime();
  };

  function formatDisplayDate(iso, strings) {
    const dt = parseISODate(iso);
    if (!dt || Number.isNaN(dt.getTime())) return '';

    const today = startOfDay(new Date());
    const tmr = startOfDay(addDays(new Date(), 1));
    const cur = startOfDay(dt);

    if (cur.getTime() === today.getTime()) return strings.today;
    if (cur.getTime() === tmr.getTime()) return strings.tomorrow;

    return dt.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }

  function formatPdpDateTitle(iso, strings) {
    const dt = parseISODate(iso);
    if (!dt || Number.isNaN(dt.getTime())) return '';

    const today = startOfDay(new Date());
    const tmr = startOfDay(addDays(new Date(), 1));
    const cur = startOfDay(dt);
    const formatted = dt.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });

    if (cur.getTime() === today.getTime()) return strings.today + ', ' + formatted;
    if (cur.getTime() === tmr.getTime()) return strings.tomorrow + ', ' + formatted;
    return formatted;
  }

  function findBellaSheet(root, which) {
    if (!root || !which) return null;
    return (
      root.querySelector('[data-bella-sheet="' + which + '"]') ||
      root.querySelector('#bella-sheet-' + which)
    );
  }

  function isPdpDeliveryRoot(root) {
    return !!(root && root.hasAttribute('data-bella-pdp-delivery'));
  }

  /** Matches `checkmark-line` in snippets/icon.liquid — used for instant stepper updates. */
  var BellaStepCheckSvg =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-checkmark-line" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12l5 5 11-12"></path></svg>';

  function monthLabel(d, locale) {
    return d.toLocaleDateString(locale || undefined, { month: 'long', year: 'numeric' });
  }

  function getBrowserTimeZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      return '';
    }
  }

  /** Long date + weekday for Shopify order "Additional details" (matches admin display). */
  function formatAdminOrderDateParts(iso) {
    const dt = parseISODate(iso);
    if (!dt || Number.isNaN(dt.getTime())) {
      return { date: '', day: '' };
    }
    const date = dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const day = dt.toLocaleDateString('en-US', { weekday: 'long' });
    return { date, day };
  }

  /** Parse admin "Delivery Date" (e.g. May 22, 2026) back to ISO for the calendar. */
  function parseAdminDeliveryDate(str) {
    if (!str || typeof str !== 'string') return '';
    const dt = new Date(str.trim());
    if (Number.isNaN(dt.getTime())) return '';
    return isoDate(dt);
  }

  const BellaLegacyAttributeKeys = [
    'delivery-region',
    'delivery-date',
    'Delivery Method',
    'Delivery area',
    'bella-gift-note',
    'Gift Note',
  ];

  /** Whole-dirham amounts omit trailing .00 (matches bella-money-amount.liquid). */
  function bellaFormatAmount(amountCents) {
    const cents = parseInt(amountCents, 10) || 0;
    if (cents % 100 === 0) {
      return String(cents / 100);
    }
    if (typeof theme !== 'undefined' && theme.Currency) {
      return theme.Currency.formatMoney(cents, '{{amount}}');
    }
    return (cents / 100).toFixed(2);
  }

  /** Subtotal / delivery / checkout: prefix + amount only (no trailing ISO like AED). */
  function bellaMoneyMarkup(amountCents, form, wrap) {
    const prefix =
      form && form.dataset && form.dataset.bellaMoneyPrefix ? form.dataset.bellaMoneyPrefix : 'Dhs.';
    const safePrefix = String(prefix)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
    const amount = bellaFormatAmount(amountCents);
    const core = '<span class="bella-money__prefix">' + safePrefix + '</span><bdi>' + amount + '</bdi>';
    if (wrap === 'core') return core;
    if (wrap === 'price') return '<span class="price bella-money">' + core + '</span>';
    return '<span class="bella-money">' + core + '</span>';
  }

  /**
   * Cart attributes for order "Additional details" — one field each; gift message uses cart.note instead.
   * @param {HTMLFormElement} form
   */
  function buildBellaCartAttributesPayload(form) {
    const region = form.dataset.deliveryRegion || 'dubai';
    const iso = form.dataset.deliveryDate || '';
    const deliveryRegionLabel = region === 'other' ? 'Other Emirates' : 'Dubai';
    const parts = formatAdminOrderDateParts(iso);
    const attrs = {
      'Delivery region': deliveryRegionLabel,
      'Delivery Date': parts.date,
      'Delivery Day': parts.day,
      'Customer TimeZone': getBrowserTimeZone(),
      'delivery-date': iso,
    };
    BellaLegacyAttributeKeys.forEach(function (k) {
      if (k === 'delivery-date') return;
      attrs[k] = '';
    });
    return attrs;
  }

  function getDeliveryPropLabel(form) {
    return (form && form.dataset.deliveryPropLabel) || 'Delivery Date';
  }

  function getGiftPropLabel(form) {
    return (form && form.dataset.giftPropLabel) || 'Gift note';
  }

  function getGiftPropYes(form) {
    return (form && form.dataset.giftPropYes) || 'Yes';
  }

  function getGiftPropNo(form) {
    return (form && form.dataset.giftPropNo) || 'No';
  }

  function buildCheckoutLinePropertyPatch(form, overrides) {
    overrides = overrides || {};
    const dateLabel = getDeliveryPropLabel(form);
    const giftLabel = getGiftPropLabel(form);
    const patch = {};

    if (Object.prototype.hasOwnProperty.call(overrides, 'date')) {
      patch[dateLabel] = overrides.date || '';
    } else {
      const parts = formatAdminOrderDateParts(form.dataset.deliveryDate || '');
      patch[dateLabel] = parts.date || '';
    }

    if (Object.prototype.hasOwnProperty.call(overrides, 'giftYes')) {
      patch[giftLabel] = overrides.giftYes ? getGiftPropYes(form) : getGiftPropNo(form);
    } else {
      const hasGift = !!(form.dataset.bellaGiftNote && String(form.dataset.bellaGiftNote).trim());
      patch[giftLabel] = hasGift ? getGiftPropYes(form) : getGiftPropNo(form);
    }

    return patch;
  }

  function applyOrderedCheckoutLineProperties(existingProps, patch, form) {
    existingProps = existingProps || {};
    const dateLabel = getDeliveryPropLabel(form);
    const giftLabel = getGiftPropLabel(form);
    const managedLabels = [dateLabel, giftLabel, 'Delivery Date', 'Gift note', 'Gift Note'];
    const next = {};

    if (patch[dateLabel]) {
      next[dateLabel] = patch[dateLabel];
    }
    if (patch[giftLabel]) {
      next[giftLabel] = patch[giftLabel];
    }

    Object.keys(existingProps).forEach(function (key) {
      if (managedLabels.indexOf(key) !== -1) return;
      if (existingProps[key] == null || existingProps[key] === '') return;
      next[key] = existingProps[key];
    });

    return next;
  }

  function checkoutLinePropertiesEqual(a, b) {
    a = a || {};
    b = b || {};
    const keys = new Set(Object.keys(a).concat(Object.keys(b)));
    for (const key of keys) {
      if ((a[key] || '') !== (b[key] || '')) return false;
    }
    return true;
  }

  let bellaLinePropsSyncChain = Promise.resolve();
  let bellaCheckoutSubmitBypass = false;

  /**
   * Checkout only shows line item properties under each product.
   * Sync Delivery Date + Gift note (Yes/No) onto every visible cart line.
   * Actual gift message stays on cart.note — this only adds a Yes/No flag for checkout.
   */
  function syncCheckoutLineProperties(form, overrides) {
    if (!form) return Promise.resolve();

    const run = function () {
      const patch = buildCheckoutLinePropertyPatch(form, overrides);
      const cartUrl = (theme.routes.cart_url || '/cart') + '.js';
      const changeUrl = theme.routes.cart_change_url || '/cart/change.js';

      return fetch(cartUrl, { credentials: 'same-origin' })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items) || !cart.items.length) return null;

          const items = cart.items.filter(function (item) {
            if (!item) return false;
            if (item.properties && item.properties._pa_hide_in_cart === 'true') return false;
            return true;
          });

          return items.reduce(function (chain, item) {
            return chain.then(function () {
              const nextProps = applyOrderedCheckoutLineProperties(item.properties, patch, form);
              if (checkoutLinePropertiesEqual(item.properties, nextProps)) return null;

              return fetch(changeUrl, {
                ...fetchConfig(),
                body: JSON.stringify({
                  id: item.key,
                  quantity: item.quantity,
                  properties: nextProps,
                }),
              }).then(function (r) {
                return r.json();
              });
            });
          }, Promise.resolve());
        })
        .catch(function (e) {
          console.error('[Bella cart] sync checkout line properties', e);
        });
    };

    bellaLinePropsSyncChain = bellaLinePropsSyncChain.then(run, run);
    return bellaLinePropsSyncChain;
  }

  /** @deprecated use syncCheckoutLineProperties */
  function syncDeliveryDateToLineItems(form, dateDisplay) {
    return syncCheckoutLineProperties(form, { date: dateDisplay || '' });
  }

  class BellaCartController {
    constructor(form) {
      this.form = form;
      this.readConfig();
      this.onKey = this.onKey.bind(this);
      this._sheetOpenTimer = null;
      this._savedMainScrollTop = null;
      this._cartUpdateAbort = null;
    }

    readConfig() {
      const ds = this.form.dataset;
      this.strings = {
        today: ds.strToday || 'Today',
        tomorrow: ds.strTomorrow || 'Tomorrow',
        selectDate: ds.strSelectDate || 'Tap to choose a date',
        giftPlaceholder: ds.strGiftPlaceholder || 'Add a message (optional)',
        checkout: ds.strCheckout || 'Checkout',
        selectDateCta: ds.strSelectDateCta || 'Select a delivery date to checkout',
      };
    }

    syncFromDom() {
      this.readConfig();
      if (!this.form.dataset.deliveryDate && this.form.dataset.deliveryDateDisplay) {
        const iso = parseAdminDeliveryDate(this.form.dataset.deliveryDateDisplay);
        if (iso) this.form.dataset.deliveryDate = iso;
      }
      if (this.form.dataset.cartNote !== undefined) {
        this.form.dataset.bellaGiftNote = this.form.dataset.cartNote || '';
      }
      this.initCalendar();
      this.initGiftField();
      this.updateFooterTotals();
    }

    onKey(e) {
      if (e.code === 'Escape') {
        this.form.querySelectorAll('.bella-sheet.is-open').forEach((s) => this.closeSheet(s.id));
      }
    }

    openSheet(which) {
      if (this._sheetOpenTimer) {
        clearTimeout(this._sheetOpenTimer);
        this._sheetOpenTimer = null;
      }
      // Defer past the opening pointer/click so the same gesture cannot hit the sheet backdrop.
      this._sheetOpenTimer = setTimeout(() => {
        this._sheetOpenTimer = null;
        const sheet = findBellaSheet(this.form, which);
        if (!sheet) return;
        const main = this.form.querySelector('.mini-cart__main');
        this._savedMainScrollTop = main ? main.scrollTop : null;
        sheet.classList.add('is-open');
        sheet.setAttribute('aria-hidden', 'false');
        document.addEventListener('keydown', this.onKey);
        if (which === 'date') {
          const iso = this.form.dataset.deliveryDate;
          if (iso) this.calSelected = parseISODate(iso);
          this.renderCalendar();
        }
        if (which === 'gift') {
          this.syncGiftCharCount();
          const ta = this.form.querySelector('[data-bella-gift-input]');
          if (ta) {
            requestAnimationFrame(() => {
              try {
                ta.focus({ preventScroll: true });
              } catch (err) {
                ta.focus();
              }
            });
          }
        }
      }, 60);
    }

    closeSheet(idOrWhich, opts) {
      opts = opts || {};
      if (this._sheetOpenTimer) {
        clearTimeout(this._sheetOpenTimer);
        this._sheetOpenTimer = null;
      }
      let sheet = null;
      let which = null;
      if (idOrWhich) {
        if (typeof idOrWhich === 'string' && (idOrWhich === 'date' || idOrWhich === 'gift')) {
          which = idOrWhich;
          sheet = findBellaSheet(this.form, which);
        } else if (typeof idOrWhich === 'string') {
          sheet = this.form.querySelector('#' + idOrWhich);
          which = sheet ? sheet.getAttribute('data-bella-sheet') : idOrWhich.replace('bella-sheet-', '');
          if (!sheet && which) sheet = findBellaSheet(this.form, which);
        }
      }
      if (sheet) {
        sheet.classList.remove('is-open');
        sheet.setAttribute('aria-hidden', 'true');
      }
      const stillOpen = this.form.querySelector('.bella-sheet.is-open');
      if (!stillOpen) {
        document.removeEventListener('keydown', this.onKey);
        if (!opts.skipScrollRestore) {
          const main = this.form.querySelector('.mini-cart__main');
          if (main && this._savedMainScrollTop != null) {
            const top = this._savedMainScrollTop;
            requestAnimationFrame(() => {
              main.scrollTop = top;
            });
          }
          const opener =
            which === 'gift'
              ? this.form.querySelector('[data-bella-open="gift"]')
              : which === 'date'
                ? this.form.querySelector('[data-bella-open="date"]')
                : null;
          if (opener) {
            requestAnimationFrame(() => {
              try {
                opener.focus({ preventScroll: true });
              } catch (err) {
                opener.focus();
              }
            });
          }
        }
        this._savedMainScrollTop = null;
      }
    }

    setRegionUI(region) {
      this.form.querySelectorAll('[data-bella-region]').forEach((b) => {
        b.classList.toggle('is-selected', b.getAttribute('data-bella-region') === region);
      });
      const segment = this.form.querySelector('[data-bella-segment]');
      if (segment) {
        segment.classList.toggle('is-region-other', region === 'other');
      }
      const dubaiBox = this.form.querySelector('[data-bella-info="dubai"]');
      const otherBox = this.form.querySelector('[data-bella-info="other"]');
      if (dubaiBox && otherBox) {
        dubaiBox.hidden = region !== 'dubai';
        otherBox.hidden = region !== 'other';
      }
      this.form.dataset.deliveryRegion = region;
      this.updateFooterTotals();
    }

    persistAttributes(attrs, opts) {
      opts = opts || {};
      if (this._cartUpdateAbort) {
        try {
          this._cartUpdateAbort.abort();
        } catch (err) {
          /* ignore */
        }
      }
      this._cartUpdateAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const signal = this._cartUpdateAbort ? this._cartUpdateAbort.signal : undefined;

      const fullAttrs = buildBellaCartAttributesPayload(this.form);
      const body = cartUpdateBody({ attributes: fullAttrs }, { attributesOnly: true });
      const fetchOpts = { ...fetchConfig(), body };
      if (signal) fetchOpts.signal = signal;

      fetch(theme.routes.cart_update_url, fetchOpts)
        .then((r) => r.json())
        .then((parsed) => {
          if (signal && signal.aborted) return;
          if (parsed && parsed.errors) {
            console.error('[Bella cart] cart/update', parsed.errors);
            if (opts.revertOnError) {
              const r = opts.revertOnError;
              if (Object.prototype.hasOwnProperty.call(r, 'deliveryDate')) {
                this.form.dataset.deliveryDate = r.deliveryDate;
              }
              this.updateFooterTotals();
              this.initCalendar();
            }
            return;
          }
          if (!opts.alreadyClosed && opts.closeSheet) {
            this.closeSheet(opts.closeSheet, { skipScrollRestore: true });
          }
          syncBellaCartAfterAttributeUpdate(parsed);
          // Line properties are what checkout shows under each product name.
          return syncCheckoutLineProperties(this.form, {
            date: fullAttrs['Delivery Date'] || '',
          });
        })
        .catch((e) => {
          if (e && e.name === 'AbortError') return;
          console.error('[Bella cart] fetch', e);
          if (opts.revertOnError) {
            const r = opts.revertOnError;
            if (Object.prototype.hasOwnProperty.call(r, 'deliveryDate')) {
              this.form.dataset.deliveryDate = r.deliveryDate;
            }
            this.updateFooterTotals();
            this.initCalendar();
          }
        });
    }

    persistCartNote(note, opts) {
      opts = opts || {};
      if (this._cartUpdateAbort) {
        try {
          this._cartUpdateAbort.abort();
        } catch (err) {
          /* ignore */
        }
      }
      this._cartUpdateAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const signal = this._cartUpdateAbort ? this._cartUpdateAbort.signal : undefined;
      const body = cartUpdateBody({ note: note }, { attributesOnly: true });
      const fetchOpts = { ...fetchConfig(), body };
      if (signal) fetchOpts.signal = signal;
      const hasGift = !!(note && String(note).trim());

      fetch(theme.routes.cart_update_url, fetchOpts)
        .then((r) => r.json())
        .then((parsed) => {
          if (signal && signal.aborted) return;
          if (parsed && parsed.errors) {
            console.error('[Bella cart] cart/update note', parsed.errors);
            if (opts.revertOnError !== undefined) {
              this.form.dataset.bellaGiftNote = opts.revertOnError;
              this.updateFooterTotals();
              this.initGiftField();
            }
            return;
          }
          if (parsed.note !== undefined) {
            this.form.dataset.bellaGiftNote = parsed.note || '';
          }
          this.updateFooterTotals();
          this.initGiftField();
          syncBellaCartAfterAttributeUpdate(parsed);
          // Checkout shows Yes/No under the product; full message stays on cart.note.
          return syncCheckoutLineProperties(this.form, { giftYes: hasGift });
        })
        .catch((e) => {
          if (e && e.name === 'AbortError') return;
          console.error('[Bella cart] fetch note', e);
          if (opts.revertOnError !== undefined) {
            this.form.dataset.bellaGiftNote = opts.revertOnError;
            this.updateFooterTotals();
            this.initGiftField();
          }
        });
    }

    saveDate() {
      if (!this.calSelected) return;
      const region = this.form.dataset.deliveryRegion || 'dubai';
      if (isBeforeMinSelectable(this.calSelected, region)) return;
      const iso = isoDate(this.calSelected);
      const prev = this.form.dataset.deliveryDate || '';
      this.form.dataset.deliveryDate = iso;
      this.updateFooterTotals();
      this.closeSheet('date', { skipScrollRestore: true });
      this.persistAttributes({}, { alreadyClosed: true, revertOnError: { deliveryDate: prev } });
    }

    saveGift() {
      const ta = this.form.querySelector('[data-bella-gift-input]');
      const val = ta ? ta.value.trim() : '';
      const prev = this.form.dataset.bellaGiftNote || '';
      this.form.dataset.bellaGiftNote = val;
      this.updateFooterTotals();
      this.closeSheet('gift', { skipScrollRestore: true });
      this.persistCartNote(val, { revertOnError: prev });
    }

    initGiftField() {
      const ta = this.form.querySelector('[data-bella-gift-input]');
      const saved = this.form.dataset.bellaGiftNote || '';
      if (ta) ta.value = saved;
      this.syncGiftCharCount();
    }

    syncGiftCharCount() {
      const ta = this.form.querySelector('[data-bella-gift-input]');
      const countEl = this.form.querySelector('[data-bella-gift-count]');
      if (!ta || !countEl) return;
      const max = parseInt(ta.getAttribute('maxlength') || '500', 10) || 500;
      const n = ta.value.length;
      countEl.textContent = n + '/' + max;
    }

    initCalendar() {
      this.calView = new Date();
      const iso = this.form.dataset.deliveryDate;
      const region = this.form.dataset.deliveryRegion || 'dubai';
      if (iso) {
        const p = parseISODate(iso);
        if (p) {
          const cur = startOfDay(p);
          if (isBeforeMinSelectable(cur, region)) {
            this.form.dataset.deliveryDate = '';
            this.calSelected = null;
            this.calView = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
          } else {
            this.calSelected = p;
            this.calView = new Date(p.getFullYear(), p.getMonth(), 1);
          }
        }
      } else {
        this.calSelected = null;
        this.calView = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      }
    }

    renderCalendar() {
      const grid = this.form.querySelector('[data-bella-cal-grid]');
      const monthEl = this.form.querySelector('[data-bella-cal-month]');
      if (!grid || !monthEl) return;

      const view = this.calView;
      monthEl.textContent = monthLabel(view, document.documentElement.lang);

      const y = view.getFullYear();
      const m = view.getMonth();
      const firstDow = new Date(y, m, 1).getDay();
      const daysInMonth = new Date(y, m + 1, 0).getDate();
      const today = startOfDay(new Date());
      const region = this.form.dataset.deliveryRegion || 'dubai';
      const minSelectable = getMinSelectableDate(region);

      let html = '';
      for (let i = 0; i < firstDow; i++) {
        html += '<span class="bella-cal__pad"></span>';
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const cell = new Date(y, m, d);
        const cur = startOfDay(cell);
        const isPast = cur.getTime() < minSelectable.getTime();
        const isToday = cur.getTime() === today.getTime();
        const isSel =
          this.calSelected && startOfDay(this.calSelected).getTime() === cur.getTime();
        let cls = 'bella-cal__day';
        if (isToday) cls += ' is-today';
        if (isPast) cls += ' is-muted';
        if (isSel) cls += ' is-selected';
        const disabled = isPast ? ' disabled' : '';
        html += '<button type="button" class="' + cls + '" data-cal-day="' + d + '"' + disabled + '>' + d + '</button>';
      }
      grid.innerHTML = html;

      const self = this;
      grid.querySelectorAll('[data-cal-day]').forEach((btn) => {
        btn.addEventListener(
          'click',
          (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            const d = parseInt(btn.getAttribute('data-cal-day'), 10);
            self.calSelected = new Date(y, m, d);
            self.renderCalendar();
          },
          true
        );
      });

      const saveBtn = this.form.querySelector('[data-bella-save-date]');
      if (saveBtn) saveBtn.disabled = !this.calSelected;
    }

    calStepMonths(delta) {
      this.calView.setMonth(this.calView.getMonth() + delta);
      this.renderCalendar();
    }

    cartDeliveryReady() {
      const mode = this.form.dataset.bellaCartMode || 'standard';
      if (mode === 'preorder-only') return true;
      return !!(this.form.dataset.deliveryDate && this.form.dataset.deliveryDate.length);
    }

    updateStepper() {
      const hasDate = this.cartDeliveryReady();
      const hasGift = !!(this.form.dataset.bellaGiftNote && this.form.dataset.bellaGiftNote.length);
      const preorderOnly = (this.form.dataset.bellaCartMode || 'standard') === 'preorder-only';
      const stepper = this.form.querySelector('[data-bella-stepper]');
      if (!stepper) return;

      stepper.style.setProperty('--bella-stepper-line-fill', hasDate ? '1' : '0.5');

      const s1 = stepper.querySelector('[data-step="1"]');
      const s2 = stepper.querySelector('[data-step="2"]');
      const s3 = stepper.querySelector('[data-step="3"]');

      if (s1) {
        s1.classList.add('is-done');
        s1.classList.remove('is-active');
      }
      if (s2) {
        s2.classList.toggle('is-done', hasDate);
        s2.classList.toggle('is-active', !preorderOnly && !hasDate);
      }
      if (s3) {
        s3.classList.toggle('is-done', hasGift);
        s3.classList.toggle('is-active', hasDate && !hasGift);
      }

      const c2 = s2 && s2.querySelector('.bella-cart__step-circle');
      if (c2) {
        if (hasDate) c2.innerHTML = BellaStepCheckSvg;
        else c2.textContent = '2';
      }
      const c3 = s3 && s3.querySelector('.bella-cart__step-circle');
      if (c3) {
        if (hasGift) c3.innerHTML = BellaStepCheckSvg;
        else c3.textContent = '3';
      }
    }

    updateFooterTotals() {
      const subCents = parseInt(this.form.dataset.subtotalCents || '0', 10) || 0;
      const region = this.form.dataset.deliveryRegion || 'dubai';
      const minFree = parseInt(this.form.dataset.freeShipMinCents || '0', 10) || 0;
      const dubaiFee = parseInt(this.form.dataset.dubaiFeeCents || '0', 10) || 0;
      const otherFee = parseInt(this.form.dataset.otherFeeCents || '0', 10) || 0;
      const preorderOnly = (this.form.dataset.bellaCartMode || 'standard') === 'preorder-only';
      const hasDate = this.cartDeliveryReady();

      let deliveryCents = 0;
      if (hasDate && !preorderOnly) {
        if (region === 'other') {
          deliveryCents = otherFee;
        } else if (minFree > 0 && subCents >= minFree) {
          deliveryCents = 0;
        } else {
          deliveryCents = dubaiFee;
        }
      }

      const delEl = this.form.querySelector('[data-bella-delivery-display]');
      const valuesEl = this.form.querySelector('.bella-cart__summary-values');
      const pickDate = this.form.dataset.strPickDate || ' + Pick a date';
      const plusFree = this.form.dataset.strPlusFree || ' + Free';
      const onItems = 'On items';

      if (delEl) {
        delEl.classList.toggle('is-free', hasDate && deliveryCents === 0 && !preorderOnly);
        if (valuesEl) valuesEl.classList.toggle('bella-cart__summary-values--muted', !hasDate);
        if (preorderOnly) {
          delEl.textContent = onItems;
        } else if (!hasDate) {
          delEl.textContent = pickDate;
        } else if (deliveryCents === 0) {
          delEl.textContent = plusFree;
        } else {
          delEl.innerHTML = '+ ' + bellaMoneyMarkup(deliveryCents, this.form, 'block');
        }
      }

      const subtotalEl = this.form.querySelector('#mini-cart-subtotal');
      if (subtotalEl) {
        subtotalEl.className = 'bella-money bella-cart__summary-amount';
        subtotalEl.id = 'mini-cart-subtotal';
        subtotalEl.innerHTML = bellaMoneyMarkup(subCents, this.form, 'core');
      }

      const btn = this.form.querySelector('[data-bella-checkout]');
      if (btn) {
        const labelEl = btn.querySelector('[data-bella-checkout-label]');
        if (!hasDate) {
          btn.disabled = true;
          if (labelEl) labelEl.textContent = this.strings.selectDateCta;
        } else {
          btn.disabled = false;
          if (labelEl) labelEl.textContent = this.strings.checkout;
        }
      }

      const rowSub = this.form.querySelector('[data-bella-date-row-sub]');
      const rowTitle = this.form.querySelector('[data-bella-date-row-title]');
      const iso = this.form.dataset.deliveryDate;
      const isPdp = isPdpDeliveryRoot(this.form);
      const changeDateLabel = this.form.dataset.strChangeDate || 'Change delivery date';
      const deliveryDateLabel = this.form.dataset.strSelectDateLabel || 'Delivery date';

      if (rowTitle && isPdp) {
        rowTitle.textContent = iso ? formatPdpDateTitle(iso, this.strings) : deliveryDateLabel;
      }

      if (rowSub) {
        if (isPdp) {
          rowSub.textContent = iso ? changeDateLabel : this.strings.selectDate;
        } else {
          rowSub.textContent = iso ? formatDisplayDate(iso, this.strings) : this.strings.selectDate;
        }
        rowSub.classList.toggle('is-accent', !!iso);
      }

      const giftSub = this.form.querySelector('[data-bella-gift-row-sub]');
      if (giftSub) {
        const g = this.form.dataset.bellaGiftNote || '';
        giftSub.textContent = g || this.strings.giftPlaceholder;
        giftSub.classList.toggle('is-accent', !!g);
      }

      const shipRoot = this.form.querySelector('.free-shipping');
      if (shipRoot && minFree > 0) {
        const pct = Math.min(100, Math.round((subCents / minFree) * 100));
        const bar = shipRoot.querySelector('.free-shipping__progress');
        if (bar) {
          bar.style.setProperty('--progress', pct + '%');
          if (pct >= 100) {
            bar.classList.add('free-shipping__progress--success');
          } else {
            bar.classList.remove('free-shipping__progress--success');
          }
        }
      }

      this.updateStepper();
    }
  }

  function isNavigationReload() {
    try {
      if (typeof performance !== 'undefined' && performance.getEntriesByType) {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.type === 'reload') return true;
      }
      if (
        typeof performance !== 'undefined' &&
        performance.navigation &&
        performance.navigation.type === 1
      ) {
        return true;
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  function clearBellaCartAttributesOnReload() {
    if (!isNavigationReload()) return Promise.resolve();

    const empty = {
      'Delivery region': '',
      'Delivery Date': '',
      'Delivery Day': '',
      'Customer TimeZone': '',
    };
    BellaLegacyAttributeKeys.forEach(function (k) {
      empty[k] = '';
    });
    const body = cartUpdateBody({ attributes: empty, note: '' }, { attributesOnly: true });
    return fetch(theme.routes.cart_update_url, { ...fetchConfig(), body })
      .then(function (r) {
        return r.json();
      })
      .then(function (parsed) {
        if (parsed && parsed.errors) {
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[Bella cart] clear on reload', parsed.errors);
          }
          return;
        }
        const form = getForm();
        if (form) {
          form.dataset.deliveryDate = '';
          form.dataset.deliveryRegion = 'dubai';
          form.dataset.bellaGiftNote = '';
          if (typeof parsed.total_price === 'number') {
            form.dataset.subtotalCents = String(parsed.total_price);
          }
          const ctrl = getCtrl(form, false);
          ctrl.setRegionUI('dubai');
          ctrl.updateFooterTotals();
          ctrl.updateStepper();
        }
        document.querySelectorAll('[data-bella-pdp-delivery]').forEach(function (root) {
          root.dataset.deliveryDate = '';
          root.dataset.deliveryRegion = 'dubai';
          root.dataset.bellaGiftNote = '';
          const ctrl = getCtrl(root, false);
          ctrl.setRegionUI('dubai');
          ctrl.updateFooterTotals();
        });
        updateCartIconSections(parsed);
        if (form) {
          return syncCheckoutLineProperties(form, { date: '', giftYes: false });
        }
      });
  }

  function getCtrl(form, forceSync) {
    let c = formControllers.get(form);
    if (!c) {
      c = new BellaCartController(form);
      formControllers.set(form, c);
      c.syncFromDom();
    } else if (forceSync) {
      c.syncFromDom();
    }
    return c;
  }

  function onBellaDeliveryClick(e, root) {
    if (!root || !root.contains(e.target)) return;

    const openBtn = e.target.closest('[data-bella-open]');
    if (openBtn) {
      e.preventDefault();
      e.stopPropagation();
      getCtrl(root, false).openSheet(openBtn.getAttribute('data-bella-open'));
      return;
    }

    const regionBtn = e.target.closest('[data-bella-region]');
    if (regionBtn && regionBtn.matches('button')) {
      e.preventDefault();
      e.stopPropagation();
      const region = regionBtn.getAttribute('data-bella-region');
      const current = root.dataset.deliveryRegion || 'dubai';
      if (region === current) return;

      const ctrl = getCtrl(root, false);
      ctrl.setRegionUI(region);
      if (root.dataset.deliveryDate) {
        const p = parseISODate(root.dataset.deliveryDate);
        if (p && isBeforeMinSelectable(p, region)) {
          root.dataset.deliveryDate = '';
          ctrl.calSelected = null;
          ctrl.initCalendar();
          ctrl.updateFooterTotals();
        }
      }
      const dateSheet = findBellaSheet(root, 'date');
      if (dateSheet && dateSheet.classList.contains('is-open')) {
        ctrl.renderCalendar();
      }
      ctrl.persistAttributes({});
      return;
    }

    const closeEl = e.target.closest('[data-bella-close]');
    if (closeEl) {
      const sheet = closeEl.closest('.bella-sheet');
      if (sheet && root.contains(sheet)) {
        e.preventDefault();
        e.stopPropagation();
        const which = sheet.getAttribute('data-bella-sheet');
        getCtrl(root, false).closeSheet(which || sheet.id);
      }
      return;
    }

    const openSheet = root.querySelector('.bella-sheet.is-open');

    if (openSheet) {
      const panel = openSheet.querySelector('.bella-sheet__panel');
      const clickedInsidePanel = panel && panel.contains(e.target);
      const clickedOpenButton = e.target.closest('[data-bella-open]');

      if (!clickedInsidePanel && !clickedOpenButton) {
        e.preventDefault();
        e.stopPropagation();
        const which = openSheet.getAttribute('data-bella-sheet');
        getCtrl(root, false).closeSheet(which || openSheet.id);
        return;
      }
    }

    const saveDateBtn = e.target.closest('[data-bella-save-date]');
    if (saveDateBtn) {
      e.preventDefault();
      e.stopPropagation();
      getCtrl(root, false).saveDate();
      return;
    }

    const saveGiftBtn = e.target.closest('[data-bella-save-gift]');
    if (saveGiftBtn) {
      e.preventDefault();
      e.stopPropagation();
      getCtrl(root, false).saveGift();
      return;
    }

    const prev = e.target.closest('[data-bella-cal-prev]');
    if (prev) {
      e.preventDefault();
      e.stopPropagation();
      getCtrl(root, false).calStepMonths(-1);
      return;
    }

    const next = e.target.closest('[data-bella-cal-next]');
    if (next) {
      e.preventDefault();
      e.stopPropagation();
      getCtrl(root, false).calStepMonths(1);
      return;
    }
  }

  function onMiniCartClick(e) {
    const host = getMiniCartHost();
    if (!host || e.currentTarget !== host) return;

    if (bellaCheckoutSubmitBypass) return;

    const checkoutBtn = e.target.closest('[data-bella-checkout]');
    if (checkoutBtn && !checkoutBtn.disabled) {
      e.preventDefault();
      e.stopPropagation();
      handleBellaCheckout(checkoutBtn);
      return;
    }

    const form = getForm();
    if (!form) return;
    onBellaDeliveryClick(e, form);
  }

  function handleBellaCheckout(btn) {
    const form = getForm();
    if (!form || !btn || btn.disabled) return;

    const labelEl = btn.querySelector('[data-bella-checkout-label]');
    const prevLabel = labelEl ? labelEl.textContent : '';
    btn.disabled = true;
    if (labelEl) labelEl.textContent = '…';

    syncCheckoutLineProperties(form)
      .then(function () {
        bellaCheckoutSubmitBypass = true;
        btn.disabled = false;
        if (labelEl) labelEl.textContent = prevLabel;
        btn.click();
        bellaCheckoutSubmitBypass = false;
      })
      .catch(function (e) {
        console.error('[Bella cart] checkout sync', e);
        btn.disabled = false;
        if (labelEl) labelEl.textContent = prevLabel;
      });
  }

  function onBellaDeliveryInput(e, root) {
    if (!root || !root.contains(e.target)) return;
    if (e.target.matches('[data-bella-gift-input]')) {
      getCtrl(root, false).syncGiftCharCount();
    }
  }

  function onMiniCartInput(e) {
    const host = getMiniCartHost();
    if (!host || e.currentTarget !== host) return;
    const form = getForm();
    if (!form) return;
    onBellaDeliveryInput(e, form);
  }

  function ensureHostDelegation() {
    const host = getMiniCartHost();
    if (!host || host.dataset.bellaDelegated === '1') return;
    host.dataset.bellaDelegated = '1';
    host.addEventListener('click', onMiniCartClick, true);
    host.addEventListener('input', onMiniCartInput, false);
  }

  function initPdpDeliveryRoots() {
    document.querySelectorAll('[data-bella-pdp-delivery]').forEach(function (root) {
      if (root.dataset.bellaDelegated === '1') return;
      root.dataset.bellaDelegated = '1';
      root.addEventListener(
        'click',
        function (e) {
          onBellaDeliveryClick(e, root);
        },
        true
      );
      root.addEventListener(
        'input',
        function (e) {
          onBellaDeliveryInput(e, root);
        },
        false
      );
      getCtrl(root, true);
    });
  }

  function refreshBellaForm() {
    const form = getForm();
    if (form) {
      getCtrl(form, true);
      syncCheckoutLineProperties(form);
    }
    initPdpDeliveryRoots();
  }

  function boot() {
    ensureHostDelegation();
    clearBellaCartAttributesOnReload()
      .catch(function () {})
      .finally(function () {
        refreshBellaForm();
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('cartdrawer:opened', refreshBellaForm);

  const host = getMiniCartHost();
  if (host) {
    let moTimer;
    const mo = new MutationObserver(() => {
      clearTimeout(moTimer);
      moTimer = setTimeout(refreshBellaForm, 30);
    });
    mo.observe(host, { childList: true, subtree: false });
  }

  // Remove the checkmark raw symbol (✓ or ✔) from the app's free shipping message in a translation-safe way
  (function() {
    const checkmarkObserver = new MutationObserver(() => {
      const lead = document.querySelector('.decoded-llp-free-shipping__unlocked-lead');
      if (lead && (lead.textContent.includes('✓') || lead.textContent.includes('✔'))) {
        checkmarkObserver.disconnect();
        lead.textContent = lead.textContent.replace(/[✓✔]/g, '').trim();
        checkmarkObserver.observe(document.body, { childList: true, subtree: true });
      }
    });
    checkmarkObserver.observe(document.body, { childList: true, subtree: true });
  })();

  // Fix the sticky media gallery position when the app's custom cart drawer is open (body is fixed)
  (function() {
    function updateGalleryPosition() {
      const isAppDrawerOpen = document.body.classList.contains('decoded-llp-drawer-open');
      const gallery = document.querySelector('.product__media-gallery');
      if (gallery) {
        if (isAppDrawerOpen) {
          const wrapper = gallery.closest('.product__media-wrapper');
          if (wrapper) {
            requestAnimationFrame(() => {
              if (!document.body.classList.contains('decoded-llp-drawer-open')) return;
              const wrapperRect = wrapper.getBoundingClientRect();
              const maxOffset = Math.max(0, wrapper.clientHeight - gallery.clientHeight);
              const offset = Math.min(maxOffset, Math.max(0, -wrapperRect.top));
              gallery.style.transform = 'translateY(' + offset + 'px)';
            });
          }
        } else {
          gallery.style.transform = '';
        }
      }
    }

    const galleryObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          updateGalleryPosition();
        }
      });
    });

    galleryObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('resize', updateGalleryPosition);
  })();
})();
